import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, LayoutGrid, List as ListIcon, Bookmark, Columns2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { ListingCard } from '@/components/common/ListingCard';
import { SkeletonGrid } from '@/components/common/SkeletonCard';
import { EmptyState } from '@/components/common/EmptyState';
import { AdSlot } from '@/components/common/AdSlot';
import { AppPagination } from '@/components/common/AppPagination';
import { FilterControls } from '@/components/filters/FilterControls';
import { AppliedChips } from '@/components/filters/AppliedChips';
import { SortSelect } from '@/components/filters/SortSelect';
import { ListingTypeToggle, getListingType } from '@/components/filters/ListingTypeToggle';
import { cn } from '@/lib/utils';
import { getListings } from '@/services/api';
import { createSavedSearch } from '@/services/consumerApi';
import { paramsToFilters, filtersToParams, emptyFilters, countActiveFilters } from '@/utils/filters';
import { useAuth } from '@/store/useAuth';
import { CompareView } from '@/components/compare/CompareView';
import { isSafeReturnPath } from '@/utils/panelRedirect';

const PAGE_SIZE = 12;

export default function Listings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [view, setView] = useState('compare');
  const [filterOpen, setFilterOpen] = useState(false);

  const { isAuthenticated } = useAuth();
  const [savingSearch, setSavingSearch] = useState(false);

  const filters = useMemo(() => paramsToFilters(searchParams), [searchParams]);
  const sort = searchParams.get('sort') || 'newest';
  const page = Number(searchParams.get('page')) || 1;
  const activeCount = countActiveFilters(filters);
  const searchKey = searchParams.toString();

  // Fetch whenever the URL (source of truth) changes.
  useEffect(() => {
    setLoading(true);
    setError(false);
    let alive = true;
    getListings({ filters, sort, page, pageSize: PAGE_SIZE })
      .then((res) => {
        if (!alive) return;
        setData(res);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setData(null);
        setError(true);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [searchKey, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = useCallback((nextFilters, extra = {}) => {
    const params = filtersToParams(nextFilters);
    if (sort !== 'newest') params.sort = sort;
    Object.assign(params, extra);
    setSearchParams(params, { replace: true });
  }, [setSearchParams, sort]);

  const patchFilter = (patch) => commit({ ...filters, ...patch }); // resets to page 1
  const setSort = (v) => { const p = filtersToParams(filters); if (v !== 'newest') p.sort = v; if (page > 1) p.page = String(page); setSearchParams(p, { replace: true }); };
  const setPage = (p) => { const params = filtersToParams(filters); if (sort !== 'newest') params.sort = sort; if (p > 1) params.page = String(p); setSearchParams(params); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const removeChip = (key, value) => {
    if (Array.isArray(key)) return patchFilter(Object.fromEntries(key.map((k) => [k, ''])));
    if (value != null) return patchFilter({ [key]: (filters[key] || []).filter((v) => v !== value) });
    patchFilter({ [key]: '' });
  };
  const clearAll = () => setSearchParams({}, { replace: true });

  const handleSaveSearch = async () => {
    if (!isAuthenticated) {
      toast('Sign in to save searches', {
        description: 'Create a free account to get email alerts.',
        action: {
          label: 'Sign in',
          onClick: () => {
            const returnTo = `${window.location.pathname}${window.location.search}`;
            const safe = isSafeReturnPath(returnTo) ? returnTo : '/listings';
            window.location.href = `/login?redirect=${encodeURIComponent(safe)}`;
          },
        },
      });
      return;
    }
    setSavingSearch(true);
    try {
      await createSavedSearch(buildSearchLabel(filters), filtersToParams(filters));
      toast('Search saved', { description: 'Manage alerts from your dashboard.' });
    } catch {
      toast.error('Could not save search. Please try again.');
    } finally {
      setSavingSearch(false);
    }
  };

  const items = data?.items || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="lg:grid lg:grid-cols-[300px_1fr] lg:gap-8">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-20 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
              <div className="mt-4"><FilterControls filters={filters} onChange={patchFilter} /></div>
            </div>
            <AdSlot slotId="listings-sidebar-c" size="box" />
          </div>
        </aside>

        {/* Results */}
        <div>
          {/* Toolbar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="font-display text-2xl font-semibold text-slate-900">Homes {filters.cityId ? `in ${capitalize(filters.cityId)}` : 'for you'}</h1>
              <p className="text-sm text-slate-600" data-testid="results-count">{loading ? 'Searching…' : `${data?.total ?? 0} results`}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ListingTypeToggle
                value={getListingType(filters)}
                onChange={(type) => patchFilter({ listingType: [type] })}
              />
              <Button variant="outline" size="sm" className="rounded-xl" onClick={handleSaveSearch} disabled={savingSearch} data-testid="save-search-button"><Bookmark size={16} className="mr-1.5" />{savingSearch ? 'Saving…' : 'Save search'}</Button>
              <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-xl lg:hidden" data-testid="listings-open-filters-button">
                    <SlidersHorizontal size={16} className="mr-1.5" />Filters{activeCount ? ` (${activeCount})` : ''}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[340px] overflow-y-auto">
                  <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
                  <div className="mt-6"><FilterControls filters={filters} onChange={patchFilter} /></div>
                  <Button className="mt-6 w-full rounded-xl" onClick={() => setFilterOpen(false)}>Show {data?.total ?? 0} results</Button>
                </SheetContent>
              </Sheet>
              <SortSelect value={sort} onChange={setSort} />
              <button
                onClick={() => setView(view === 'compare' ? 'grid' : 'compare')}
                className={cn(
                  'hidden sm:inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                  view === 'compare'
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
                )}
              >
                <Columns2 size={15} />
                Compare
              </button>
              <div className="hidden sm:flex overflow-hidden rounded-xl border border-slate-200">
                <button onClick={() => setView('grid')} className={cn('grid h-9 w-9 place-items-center', view === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600')} aria-label="Grid view" data-testid="view-grid"><LayoutGrid size={16} /></button>
                <button onClick={() => setView('list')} className={cn('grid h-9 w-9 place-items-center', view === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600')} aria-label="List view" data-testid="view-list"><ListIcon size={16} /></button>
                <button onClick={() => setView('compare')} className={cn('grid h-9 w-9 place-items-center', view === 'compare' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600')} aria-label="Compare view" data-testid="view-compare"><Columns2 size={16} /></button>
              </div>
            </div>
          </div>

          {/* Applied chips */}
          <div className="mt-4"><AppliedChips filters={filters} onRemove={removeChip} onClearAll={clearAll} /></div>

          {/* Grid / list */}
          <div className="mt-6">
            {loading ? (
              <SkeletonGrid count={6} />
            ) : error ? (
              <EmptyState
                title="Could not load listings"
                description="Something went wrong while loading homes. Please try again."
                actionLabel="Try again"
                onAction={() => setRetryKey((k) => k + 1)}
              />
            ) : items.length === 0 ? (
              <EmptyState actionLabel="Clear all filters" onAction={clearAll} />
            ) : view === 'compare' ? (
              <CompareView items={items} />
            ) : (
              <div className={cn(view === 'grid' ? 'grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3' : 'flex flex-col gap-5')}>
                {items.map((l, i) => (
                  <div key={l.id} className="contents">
                    <ListingCard listing={l} view={view} />
                    {((i + 1) % 4 === 0 || (items.length < 4 && i === items.length - 1)) && (
                      <AdSlot slotId="grid-native" size="native" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && data && data.totalPages > 1 && (
            <div className="mt-10"><AppPagination page={page} totalPages={data.totalPages} onChange={setPage} /></div>
          )}
        </div>
      </div>
    </div>
  );
}

function capitalize(s = '') { return s.charAt(0).toUpperCase() + s.slice(1); }
function buildSearchLabel(filters) {
  const parts = [];
  if (filters.cityId) parts.push(capitalize(filters.cityId));
  else if (filters.city) parts.push(filters.city);
  if (Array.isArray(filters.status) && filters.status.length) parts.push(filters.status.join('/'));
  if (filters.minBeds) parts.push(`${filters.minBeds}+ bd`);
  return parts.length ? parts.join(' · ') : 'All homes';
}
